// useFieldConfig — fetches the admin-configurable mandatory-field map once
// per session. Used by Setup.jsx so red asterisks reflect live config.
//
// Falls back to "everything default-required" if the API errors out so
// schools can never end up with a permanently-locked Setup form.
import { useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Module-level cache so multiple components don't refetch on the same render.
let cache = null;
let inflight = null;

async function loadOnce() {
  if (cache) return cache;
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      const resp = await axios.get(`${API}/field-config`);
      cache = resp.data || { sections: {}, required: {} };
    } catch (err) {
      console.warn("field-config fetch failed, defaulting to permissive:", err?.message || err);
      cache = { sections: {}, required: {}, _error: true };
    } finally {
      inflight = null;
    }
    return cache;
  })();
  return inflight;
}

export function invalidateFieldConfig() {
  cache = null;
}

export function useFieldConfig() {
  const [data, setData] = useState(cache);
  const [loading, setLoading] = useState(!cache);

  useEffect(() => {
    if (cache) {
      setData(cache);
      setLoading(false);
      return;
    }
    let cancelled = false;
    loadOnce().then((d) => {
      if (cancelled) return;
      setData(d);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, []);

  // Helper: is the field required RIGHT NOW (per current admin config)?
  // Defaults to `fallbackRequired` if the config has not been loaded yet or
  // doesn't know about the field — keeps Setup.jsx behaving exactly as
  // before until config arrives.
  const isRequired = (section, fieldKey, fallbackRequired = false) => {
    const r = data?.required?.[section];
    if (!r || !(fieldKey in r)) return fallbackRequired;
    return !!r[fieldKey];
  };

  return { data, loading, isRequired };
}
